import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaClock, FaMapMarkerAlt, FaArrowLeft, FaShare } from 'react-icons/fa';
import SEO from '../../components/SEO';
import api from '../../services/api';
import './eventDetail.css';

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const isArabic = i18n.language === 'ar';
  
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/image/logofr.png';
    if (imagePath.startsWith('http') || imagePath.startsWith('blob:')) return imagePath;
    const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
    const baseUrl = isProduction ? '' : 'http://localhost:3002';
    return `${baseUrl}${imagePath.startsWith('/') ? imagePath : '/' + imagePath}`;
  };

  useEffect(() => {
    fetchEventDetails();
  }, [id]);

  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      const response = await api.getEvents();
      
      let eventsData = [];
      if (response.data && Array.isArray(response.data)) {
        eventsData = response.data;
      } else if (response.data && response.data.events && Array.isArray(response.data.events)) {
        eventsData = response.data.events;
      } else if (response.data && response.data.success && response.data.events && Array.isArray(response.data.events)) {
        eventsData = response.data.events;
      }

      const foundEvent = eventsData.find(e => e._id === id || e.id === id);
      
      if (foundEvent) {
        const processedEvent = {
          ...foundEvent,
          image: getImageUrl(foundEvent.image),
          gallery: foundEvent.gallery && Array.isArray(foundEvent.gallery) 
            ? foundEvent.gallery.map(img => getImageUrl(img))
            : []
        };
        setEvent(processedEvent);
      } else {
        navigate('/events');
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'ar' ? 'ar-TN' : 'fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryLabel = (category) => {
    const categories = {
      workshop: t('events.categories.workshop'),
      conference: t('events.categories.conference'),
      seminar: t('events.categories.seminar'),
      training: t('events.categories.training'),
      meeting: t('events.categories.meeting'),
      other: t('events.categories.other')
    };
    return categories[category] || category;
  };

  if (loading) {
    return (
      <div className="event-detail-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!event) {
    return null;
  }

  return (
    <>
      <SEO 
        title={isArabic && event.titleAr ? event.titleAr : event.title}
        description={isArabic && event.descriptionAr ? event.descriptionAr : event.description}
        image={event.image}
      />
      
      <div className={`event-detail-page mt-20 ${isRTL ? 'rtl' : ''}`}>
        <motion.div 
          className="event-detail- "
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="event-detail-hero">
            <img 
              src={event.image} 
              alt={isArabic && event.titleAr ? event.titleAr : event.title}
              className="event-detail-hero-image"
            />
            <div className="event-detail-hero-overlay">
              <button 
                onClick={() => navigate('/events')} 
                className="back-button"
              >
                <FaArrowLeft className={isRTL ? 'rotate-180' : ''} />
                <span>{t('events.backToEvents')}</span>
              </button>
              <div className="event-detail-hero-content">
                <span className="event-detail-category">
                  {getCategoryLabel(event.category)}
                </span>
                <h1 className="event-detail-title">{isArabic && event.titleAr ? event.titleAr : event.title}</h1>
                <div className="event-detail-meta">
                  <div className="event-detail-meta-item">
                    <FaCalendarAlt />
                    <span>{formatDate(event.date)}</span>
                  </div>
                  {event.time && (
                    <div className="event-detail-meta-item">
                      <FaClock />
                      <span>{event.time}</span>
                    </div>
                  )}
                  {(event.location || event.locationAr) && (
                    <div className="event-detail-meta-item">
                      <FaMapMarkerAlt />
                      <span>{isArabic && event.locationAr ? event.locationAr : event.location}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="event-detail-content">
            <div className="event-detail-main">
              <section className="event-detail-section">
                <h2>{t('events.description')}</h2>
                <p className="event-detail-description">{isArabic && event.descriptionAr ? event.descriptionAr : event.description}</p>
              </section>

              {((isArabic && event.outcomesAr && event.outcomesAr.length > 0) || (event.outcomes && event.outcomes.length > 0)) && (
                <section className="event-detail-section">
                  <h2>{t('events.outcomes')}</h2>
                  <ul className="event-detail-outcomes">
                    {(isArabic && event.outcomesAr && event.outcomesAr.length > 0 
                      ? event.outcomesAr 
                      : event.outcomes
                    ).filter(o => o).map((outcome, index) => (
                      <li key={index}>{outcome}</li>
                    ))}
                  </ul>
                </section>
              )}

              {event.gallery && event.gallery.length > 0 && (
                <section className="event-detail-section">
                  <h2>{t('events.gallery')}</h2>
                  <div className="event-detail-gallery">
                    {event.gallery.map((image, index) => (
                      <motion.div
                        key={index}
                        className="event-detail-gallery-item"
                        whileHover={{ scale: 1.05 }}
                        onClick={() => setSelectedImage(image)}
                      >
                        <img src={image} alt={`Gallery ${index + 1}`} />
                      </motion.div>
                    ))}
                  </div>
                </section>
              )}
            </div>

            <aside className="event-detail-sidebar">
              <div className="event-detail-info-card">
                <h3>{t('events.eventInfo')}</h3>
                <div className="event-detail-info-list">
                  <div className="event-detail-info-item">
                    <span className="label">{t('events.date')}</span>
                    <span className="value">{formatDate(event.date)}</span>
                  </div>
                  {event.time && (
                    <div className="event-detail-info-item">
                      <span className="label">{t('events.time')}</span>
                      <span className="value">{event.time}</span>
                    </div>
                  )}
                  {(event.location || event.locationAr) && (
                    <div className="event-detail-info-item">
                      <span className="label">{t('events.location')}</span>
                      <span className="value">{isArabic && event.locationAr ? event.locationAr : event.location}</span>
                    </div>
                  )}
                  <div className="event-detail-info-item">
                    <span className="label">{t('events.category')}</span>
                    <span className="value">{getCategoryLabel(event.category)}</span>
                  </div>
                  <div className="event-detail-info-item">
                    <span className="label">{t('events.status')}</span>
                    <span className={`value status-${event.status}`}>
                      {event.status === 'upcoming' ? t('events.upcoming') : t('events.past')}
                    </span>
                  </div>
                </div>
              </div>

              <div className="event-detail-tags">
                {event.health && <span className="tag">{t('events.tags.health')}</span>}
                {event.culture && <span className="tag">{t('events.tags.culture')}</span>}
                {event.sports && <span className="tag">{t('events.tags.sports')}</span>}
                {event.professional && <span className="tag">{t('events.tags.professional')}</span>}
              </div>
            </aside>
          </div>
        </motion.div>

        {selectedImage && (
          <div className="image-lightbox" onClick={() => setSelectedImage(null)}>
            <div className="lightbox-content">
              <button className="lightbox-close" onClick={() => setSelectedImage(null)}>×</button>
              <img src={selectedImage} alt="Full size" />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default EventDetail;
